import type { ChecklistItem, Task, TaskPriority, TaskStatus, TeamView } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const DEV_AUTO_LOGIN =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true';

let tokenProvider: () => Promise<string | null> = async () => null;

export function setAuthTokenProvider(
  provider: () => Promise<string | null>
): void {
  tokenProvider = provider;
}

/**
 * Core fetch wrapper. Every authenticated API call funnels through this —
 * including `fetchMe()`, the settings API client in `src/api/settings.ts`,
 * and the task-list/mutation helpers below. Do NOT re-implement
 * auth-header logic anywhere else.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (DEV_AUTO_LOGIN) {
    const { mockApiFetch } = await import('./devMockApi');
    return mockApiFetch<T>(path, init);
  }
  const token = await tokenProvider();
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch (err) {
    throw new Error(
      err instanceof TypeError
        ? "Can't reach the task API. Check your connection or try again."
        : String(err)
    );
  }
  if (!response.ok) {
    const body = await response.text();
    if (response.status === 401) {
      throw new Error('Your session expired. Sign in again to continue.');
    }
    throw new Error(`API error ${response.status}: ${body}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface MeGroup {
  id: string;
  displayName: string;
  color: string;
  kind: 'company' | 'personal';
}

export interface Me {
  sub: string;
  displayName: string;
  color: string;
  groups: MeGroup[];
  isSuperAdmin: boolean;
}

export async function fetchMe(): Promise<Me> {
  return apiFetch<Me>('/me');
}

export interface ListTasksResponse {
  tasks: Task[];
}

/**
 * List tasks. `group` is the preferred param (ADR 0009); the backend still
 * accepts `scope=` as a legacy alias. Pass one of:
 *   - `undefined` / `'combined'` — union across all the caller's partitions
 *   - `'personal'` — the caller's own personal partition
 *   - a company teamId (e.g. `'woxom-health'`)
 *   - `'deleted'` — soft-deleted tasks (Trash)
 *   - `'completed'` — completed tasks (Completed view)
 */
export async function listTasks(
  group?: string,
  opts?: { all?: boolean }
): Promise<Task[]> {
  const params = new URLSearchParams();
  if (group) params.set('group', group);
  if (opts?.all) params.set('all', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const { tasks } = await apiFetch<ListTasksResponse>(`/tasks${qs}`);
  return tasks;
}

export interface CreateTaskInput {
  teamId: string;
  title: string;
  description?: string;
  priority?: TaskPriority;
  dueDate?: string;
  assigneeId?: string;
  private?: boolean;
}

export async function createTask(input: CreateTaskInput): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>('/tasks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return task;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  priority?: TaskPriority;
  pinned?: boolean;
  dueDate?: string | null;
  status?: TaskStatus;
  assigneeId?: string;
  private?: boolean;
  /** ADR 0010: replace the whole checklist. Use toggleChecklistItem for a single flip. */
  checklist?: ChecklistItem[];
}

/**
 * Backend only reads `taskId` from the path — the first path segment is a
 * legacy `{scope}` slot retained for URL compatibility. Pass `teamId`
 * (or any stable string) there.
 */
export async function updateTask(
  teamId: string,
  taskId: string,
  patch: UpdateTaskInput
): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }
  );
  return task;
}

export async function deleteTask(teamId: string, taskId: string): Promise<void> {
  await apiFetch<void>(`/tasks/${encodeURIComponent(teamId)}/${taskId}`, {
    method: 'DELETE',
  });
}

export async function purgeTask(teamId: string, taskId: string): Promise<void> {
  await apiFetch<void>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}?purge=true`,
    { method: 'DELETE' }
  );
}

export async function restoreTask(teamId: string, taskId: string): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}/restore`,
    { method: 'POST' }
  );
  return task;
}

export async function addNote(
  teamId: string,
  taskId: string,
  body: string,
  transitionToPending = true
): Promise<Task> {
  const res = await apiFetch<{ task: Task }>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}/notes`,
    {
      method: 'POST',
      body: JSON.stringify({ body, transitionToPending }),
    }
  );
  return res.task;
}

/**
 * Move a task from its current partition to `toTeamId`. Backend validates
 * the caller is a member of the destination group.
 */
export async function moveTask(
  fromTeamId: string,
  taskId: string,
  toTeamId: string
): Promise<Task> {
  const { task } = await apiFetch<{ task: Task }>(
    `/tasks/${encodeURIComponent(fromTeamId)}/${taskId}/move`,
    {
      method: 'POST',
      body: JSON.stringify({ toTeamId }),
    }
  );
  return task;
}

export async function reclassifyTask(teamId: string, taskId: string): Promise<Task> {
  const { task } = await apiFetch<{ task: Task; reasoning?: string }>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}/reclassify`,
    { method: 'POST' }
  );
  return task;
}

export async function getTeam(): Promise<TeamView> {
  return apiFetch<TeamView>('/team');
}

export interface UserSettings {
  userId: string;
  teamId: string;
  activityLastReadAt?: string;
  updatedAt: string;
}

export async function getSettings(): Promise<UserSettings> {
  const { settings } = await apiFetch<{ settings: UserSettings }>('/settings');
  return settings;
}

export async function updateSettings(
  patch: Partial<Pick<UserSettings, 'activityLastReadAt'>>
): Promise<UserSettings> {
  const { settings } = await apiFetch<{ settings: UserSettings }>('/settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return settings;
}

export interface ActivityItem {
  eventId: string;
  type: string;
  createdAt: string;
  actorUserId: string;
  taskId: string;
  teamId: string;
  taskTitle: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export async function listActivity(limit = 30): Promise<ActivityItem[]> {
  const { activity } = await apiFetch<{ activity: ActivityItem[] }>(
    `/activity?limit=${limit}`
  );
  return activity;
}

export async function getAttachmentUrl(
  teamId: string,
  taskId: string,
  filename: string
): Promise<string> {
  const { url } = await apiFetch<{ url: string }>(
    `/tasks/${encodeURIComponent(teamId)}/${taskId}/attachments/${encodeURIComponent(filename)}`
  );
  return url;
}
